import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { accepterInvitation, verifierInvitation, type ApercuInvitation } from '../api/invitations';
import { useSession } from '../app/SessionContext';

/** Ce que l'écran sait afficher du lien. */
type Etat =
  | { readonly phase: 'chargement' }
  | { readonly phase: 'pret'; readonly apercu: ApercuInvitation }
  | { readonly phase: 'refus'; readonly message: string };

const CLASSE_CHAMP =
  'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

/**
 * Page PUBLIQUE d'acceptation d'une invitation : /invitation/:jeton.
 *
 * Le compte n'existe pas encore, donc aucune session n'est requise. Le lien ne
 * sert qu'une fois : dès que le mot de passe est choisi, il est brûlé.
 */
export function Invitation() {
  const { jeton = '' } = useParams();
  const naviguer = useNavigate();
  const { rafraichir } = useSession();
  const [etat, setEtat] = useState<Etat>({ phase: 'chargement' });
  const [motDePasse, setMotDePasse] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState<string>();
  const [envoi, setEnvoi] = useState(false);
  const premier = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const controleur = new AbortController();
    void verifierInvitation(jeton, { signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) {
          setEtat({ phase: 'pret', apercu: resultat.donnees });
        } else {
          setEtat({
            phase: 'refus',
            message: resultat.message ?? 'Ce lien d’invitation n’est plus valable.',
          });
        }
      })
      .catch(() => {
        /* abandon : composant démonté */
      });
    return () => controleur.abort();
  }, [jeton]);

  useEffect(() => {
    if (etat.phase === 'pret') premier.current?.focus();
  }, [etat.phase]);

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();

    if (motDePasse !== confirmation) {
      setErreur('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setEnvoi(true);
    setErreur(undefined);
    const resultat = await accepterInvitation(jeton, motDePasse);
    setEnvoi(false);

    if (resultat.ok) {
      // La session est ouverte par le serveur : on relit l'état avant de naviguer.
      await rafraichir();
      naviguer('/', { replace: true });
    } else {
      setErreur(resultat.message ?? 'Création du compte impossible.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--app-background)] p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-red-600" />
        <div className="p-8">
          <img
            src="/logo-registre.webp"
            alt="Registre-Sécurité — registre de sécurité incendie numérique"
            className="logo-marque mx-auto w-56 h-auto"
          />
          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.28em] text-red-600 text-center">
            Invitation
          </p>
          <h1 className="mt-1 text-lg font-semibold text-slate-900 text-center">
            Ouvrez votre espace
          </h1>

          {etat.phase === 'chargement' && (
            <p className="mt-6 text-sm text-slate-500 text-center">Vérification du lien…</p>
          )}

          {etat.phase === 'refus' && (
            <div className="mt-6" role="alert">
              <p className="text-sm text-red-700 font-medium">Lien inutilisable</p>
              <p className="mt-1 text-sm text-slate-600">{etat.message}</p>
              <a href="/connexion" className="mt-4 inline-block text-sm text-red-700 underline">
                Aller à la connexion
              </a>
            </div>
          )}

          {etat.phase === 'pret' && (
            <form onSubmit={(evenement) => void soumettre(evenement)} className="mt-6 space-y-4">
              <dl className="rounded-lg bg-slate-50 border border-slate-100 p-4 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500">Compte</dt>
                  <dd className="text-slate-800 font-medium">{etat.apercu.emailMasque}</dd>
                </div>
                <div className="flex justify-between gap-4 mt-1">
                  <dt className="text-slate-500">Rôle</dt>
                  <dd className="text-slate-800">{etat.apercu.roleLibelle}</dd>
                </div>
                <div className="flex justify-between gap-4 mt-1">
                  <dt className="text-slate-500">Valable jusqu’au</dt>
                  <dd className="text-slate-800 mono">{etat.apercu.expireLe.slice(0, 16)}</dd>
                </div>
              </dl>

              {etat.apercu.message && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-800 mb-1">
                    Message de votre administrateur
                  </p>
                  <p className="text-sm text-slate-800 whitespace-pre-line">
                    {etat.apercu.message}
                  </p>
                </div>
              )}

              <div>
                <label
                  htmlFor="invitation-mot-de-passe"
                  className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1"
                >
                  Mot de passe
                </label>
                <input
                  id="invitation-mot-de-passe"
                  ref={premier}
                  type="password"
                  value={motDePasse}
                  onChange={(evenement) => setMotDePasse(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="new-password"
                  minLength={10}
                  required
                />
                <p className="mt-1 text-xs text-slate-500">
                  Dix caractères minimum. Une phrase fait très bien l’affaire.
                </p>
              </div>

              <div>
                <label
                  htmlFor="invitation-confirmation"
                  className="block text-xs font-medium uppercase tracking-wide text-slate-500 mb-1"
                >
                  Confirmer le mot de passe
                </label>
                <input
                  id="invitation-confirmation"
                  type="password"
                  value={confirmation}
                  onChange={(evenement) => setConfirmation(evenement.target.value)}
                  className={CLASSE_CHAMP}
                  autoComplete="new-password"
                  minLength={10}
                  required
                />
              </div>

              {erreur && (
                <p role="alert" className="text-sm text-red-700">
                  {erreur}
                </p>
              )}

              <button
                type="submit"
                disabled={envoi}
                className="w-full py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60"
              >
                {envoi ? 'Création…' : 'Créer mon compte'}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Ce lien ne fonctionne qu’une fois.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
